import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

const faqs = [
  {
    question: 'Why did A-road RED condition jump from 6% to 18% in 2024?',
    answer: 'Brighton & Hove switched from SCANNER laser surveys to Annual Engineer Inspections (AEI) in the financial year ending 2024. The council states the increase in red roads "is therefore due to the change in collection method rather than a significant change in the condition of the network." It also admits SCANNER accuracy "in an urban environment is not as good as in rural settings," and that data stayed "relatively consistent between 2020 to 2023 despite there being a noticeable decline in the condition of the network." The jump reflects measurement change — but the council\'s own admission that deterioration was under-recorded for years is still relevant to Section 58.',
  },
  {
    question: 'Brighton & Hove is AMBER on every DfT scorecard — can I still claim?',
    answer: 'Yes. All four 2025/26 scorecards — overall, condition, spend and best practice — are AMBER. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired, not on aggregate ratings. Your claim should focus on prior reports, photos showing defect age, your road class, and whether the council\'s inspection regime caught the pothole in time.',
  },
  {
    question: 'What does the £96m admitted backlog mean for my claim?',
    answer: 'The council states there is "currently a £57m maintenance backlog on carriageways and £39m on footways." Combined with 2024/25 capital and revenue spend of £5.730m (£3.738m + £1.992m), clearing the full backlog would take roughly 17 years of total maintenance budget at current levels — assuming no further deterioration. The council also admits "there is not enough funding to resurface all the roads that have failed" and prioritises investigation-level defect repairs to keep roads safe until structural work can be funded. That documents reactive management on a structurally failing network.',
  },
  {
    question: 'Brighton projects 71% preventative spend for 2025/26 — does that help the council\'s defence?',
    answer: 'It is part of their asset-management story — the DfT best-practice scorecard is still AMBER. Preventative share was 65–77% across recent years, yet estimated pothole repairs rose from 3,163 in 2022/23 to 6,027 in 2024/25 (+91% from that low point). High preventative ratios alongside rising reactive patch counts suggest defects are still forming faster than lifecycle treatments stabilise the network — which does not automatically prove your specific defect was unavoidable.',
  },
  {
    question: 'What if my pothole was on a residential or unclassified road?',
    answer: 'Unclassified roads make up 480km — roughly 79% of Brighton & Hove\'s 609km carriageway network. The council publishes RED percentages only for U-roads (8% red in 2024 under AEI, down from 11–16% under SCANNER in 2020–2023 — again, methodology changed). U-roads must be surveyed at least every four years nationally; Brighton carried out a full-network survey in 2023/24 and plans annual full-network AEI from 2025/26. Claims on residential streets still turn on safety inspections, prior reports and whether your defect met intervention thresholds.',
  },
  {
    question: 'The council says structurally failed roads are patched until resurfacing is funded — is that reasonable maintenance?',
    answer: 'The council states: "Once defects begin to appear regularly on a road, the road has structurally failed and needs to be resurfaced. There is not enough funding to resurface all the roads that have failed and therefore prioritising the repair of investigation-level defects keeps the road safe until a structural resurface can be funded." That is an admission of temporary patching on failed carriageways — not proof that your specific pothole was reasonably maintained, especially if prior reports or photos show the defect predated any patch.',
  },
  {
    question: 'How do I report a pothole to Brighton & Hove City Council?',
    answer: 'Report damage to roads, pavements and potholes via the council\'s online form at brighton-hove.gov.uk. You need to provide the location, a description including size and depth, and a photo if safe. The council investigates every report under its Highway Reactive Safety Maintenance and Inspection Policy, with repair timescales of 2 hours to 28 days depending on risk. Prior reports strengthen a claim by demonstrating notice before your incident — Fixtyer searches for existing reports and attaches them to your claim pack.',
  },
];

export function BrightonAndHoveCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Brighton & Hove Pothole Claims | 6,027 Repairs & £96m Backlog | Fixtyer"
        description="Brighton & Hove City Council filled an estimated 6,027 potholes in 2024/25. DfT rates the city AMBER on all scorecards. A 2024 survey switch revealed A-roads at 18% RED. See what this means for your Section 58 claim."
        canonical="/councils/brighton-and-hove"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Brighton & Hove', url: '/councils/brighton-and-hove' },
        ]}
      />
      <FAQSchema faqs={faqs} />
      <ServiceSchema
        councilName="Brighton & Hove City Council"
        councilSlug="brighton-and-hove"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/brighton-and-hove"
        headline="Brighton & Hove Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Brighton & Hove</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3 mb-6 max-w-3xl">
            <p className="text-amber-200 text-sm">
              <strong className="text-amber-400">Methodology caveat:</strong> Brighton & Hove moved from SCANNER to
              Annual Engineer Inspections (AEI) in 2023/24. The council states 2024 red-road increases are{' '}
              <strong className="text-white">"due to the change in collection method rather than a significant change
              in the condition of the network"</strong> — but also admits SCANNER understated urban deterioration for
              years. Figures before and after 2024 are not directly comparable.
            </p>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            6,027 Pothole Repairs in 2024/25 on an All-Amber-Rated Network
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Brighton & Hove City Council maintains <strong className="text-white">609km of roads</strong> — including{' '}
            <strong className="text-rose-400">480km of unclassified streets (79% of the network)</strong>. The DfT rates
            the city <strong className="text-amber-400">AMBER</strong> on overall, condition, spend and best-practice
            scorecards for 2025/26. Its own transparency report records{' '}
            <strong className="text-rose-400">6,027 estimated pothole repairs in 2024/25</strong>, up 91% from the
            2022/23 low of 3,163, and admits a{' '}
            <strong className="text-amber-400">£96m maintenance backlog</strong> (£57m carriageways, £39m footways).
            Section 58 still turns on your specific defect.
          </p>

          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">18%</div>
            <div className="text-white font-medium mt-2">A-roads in RED condition (2024, AEI)</div>
            <div className="text-brand-300 text-sm mt-1">
              Up from 6% under SCANNER in 2020–2023 — a methodology reset the council says reflects measurement change,
              not a single-year collapse. B and C roads rose from 4% to 15% red on the same basis.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* Network scale */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            609km of Roads — Mostly Residential
          </h2>
          <p className="text-surface-600 mb-8">
            Network scale from Brighton & Hove's June 2025 transparency report — where pothole claims actually happen
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">609km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Total carriageway</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-amber-600">480km</div>
              <div className="text-sm text-amber-800 font-medium mt-1">Unclassified (U) roads</div>
              <div className="text-xs text-amber-700 mt-1">79% of the network</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">67km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">B and C roads</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">62km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">A roads</div>
              <div className="text-xs text-surface-500 mt-1">Coastal and principal corridors</div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-6">
            <table className="w-full text-sm">
              <caption className="sr-only">Brighton & Hove highway asset breakdown</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Asset</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Scale</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Footways</td>
                  <td className="px-6 py-4 text-center font-medium">976km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Public rights of way</td>
                  <td className="px-6 py-4 text-center font-medium">159km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Cycleways</td>
                  <td className="px-6 py-4 text-center font-medium">49km</td>
                </tr>
                <tr>
                  <td className="px-6 py-4">Highway structures</td>
                  <td className="px-6 py-4 text-center font-medium">51 bridges, 93 retaining walls, 3 footbridges, 11 pedestrian subways</td>
                </tr>
              </tbody>
            </table>
          </div>

          <QuoteCard
            quote="Historically, road condition data has been collected using a method known as SCANNER. In the financial year ending in 2024, the Council moved to a new method of road condition collection to improve accuracy. The increase in the percentage of red roads in this year is therefore due to the change in collection method rather than a significant change in the condition of the network."
            source="Brighton & Hove City Council Local Highways Maintenance Transparency Report (June 2025)"
          />
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What AMBER Condition Actually Shows
          </h2>
          <p className="text-surface-600 mb-8">
            SCANNER data 2020–2023, then Annual Engineer Inspections (AEI) from 2024 — not directly comparable
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <p className="text-amber-800 text-sm">
              <strong>Methodology caveat:</strong> The council admits "the accuracy of SCANNER in an urban environment
              is not as good as in rural settings" and that data "remained relatively consistent between 2020 to 2023
              despite there being a noticeable decline in the condition of the network." From 2026/27, BSI PAS 2161 will
              introduce a five-category standard — the council warns there may be "a further shift in the data before
              we can establish a baseline to compare year-on-year progress."
            </p>
          </div>

          {/* A roads */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A roads (62km)</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                    <th className="px-4 py-2 text-center font-semibold text-surface-600">Method</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">8%</td>
                    <td className="px-4 py-2 text-center">39%</td>
                    <td className="px-4 py-2 text-center">53%</td>
                    <td className="px-4 py-2 text-center text-surface-500">SCANNER</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021–2023</td>
                    <td className="px-4 py-2 text-center">6%</td>
                    <td className="px-4 py-2 text-center">32–38%</td>
                    <td className="px-4 py-2 text-center">56–62%</td>
                    <td className="px-4 py-2 text-center text-surface-500">SCANNER</td>
                  </tr>
                  <tr className="bg-rose-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">18%</td>
                    <td className="px-4 py-2 text-center font-bold">26%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">56%</td>
                    <td className="px-4 py-2 text-center font-bold text-surface-700">AEI</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              A-road red condition tripled from 6% (SCANNER) to 18% (AEI) when the survey method changed. The council
              attributes this to measurement, not a single-year deterioration event — but 18% structurally requiring
              maintenance is the published baseline under the new method.
            </p>
          </div>

          {/* B&C roads */}
          <div className="bg-white border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">B and C roads (67km)</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                    <th className="px-4 py-2 text-center font-semibold text-surface-600">Method</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020–2023</td>
                    <td className="px-4 py-2 text-center">4–5%</td>
                    <td className="px-4 py-2 text-center">27–29%</td>
                    <td className="px-4 py-2 text-center">67–69%</td>
                    <td className="px-4 py-2 text-center text-surface-500">SCANNER</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">15%</td>
                    <td className="px-4 py-2 text-center font-bold">23%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">62%</td>
                    <td className="px-4 py-2 text-center font-bold text-surface-700">AEI</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              B and C road red condition rose from 4% under SCANNER to 15% under AEI — again attributed to the survey
              switch. Fifteen per cent requiring structural maintenance on classified link roads is a material figure
              for claims on those corridors.
            </p>
          </div>

          {/* U roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-amber-900 mb-4">Unclassified roads (480km) — RED % only published</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-surface-600">Method</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">16%</td>
                    <td className="px-4 py-2 text-center text-surface-500">SCANNER</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">13%</td>
                    <td className="px-4 py-2 text-center text-surface-500">SCANNER</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">11%</td>
                    <td className="px-4 py-2 text-center text-surface-500">SCANNER</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">12%</td>
                    <td className="px-4 py-2 text-center text-surface-500">SCANNER</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">8%</td>
                    <td className="px-4 py-2 text-center font-bold text-surface-700">AEI</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              The council publishes red percentages only for U-roads — not full amber/green splits. Under AEI, U-road
              red fell to 8% from 11–16% under SCANNER, but the council warns against year-on-year comparison across
              methods. Most pothole damage happens on these 480km of residential streets.
            </p>
          </div>

          <QuoteCard
            quote="This means that the data has remained relatively consistent between 2020 to 2023 despite there being a noticeable decline in the condition of the network."
            source="Brighton & Hove City Council Local Highways Maintenance Transparency Report (June 2025)"
          />
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold mb-4">
            Following the Money
          </h2>
          <p className="text-brand-200 mb-8 max-w-2xl">
            AMBER spend scorecard — high preventative ratios, but a £96m backlog the council cannot clear at current funding
          </p>

          <div className="bg-brand-900/50 rounded-2xl border border-brand-800 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <caption className="sr-only">Brighton & Hove highway maintenance spending 2020–2026</caption>
              <thead>
                <tr className="bg-brand-800 border-b border-brand-700">
                  <th className="px-4 py-3 text-left font-semibold text-brand-300">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">DfT capital (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Capital spend (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Revenue spend (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Reactive</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-800">
                <tr className="bg-amber-900/30">
                  <td className="px-4 py-3 font-medium">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center">5,283</td>
                  <td className="px-4 py-3 text-center font-bold text-amber-400">5,283</td>
                  <td className="px-4 py-3 text-center">2,210</td>
                  <td className="px-4 py-3 text-center text-amber-400">71%</td>
                  <td className="px-4 py-3 text-center">29%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2024/25</td>
                  <td className="px-4 py-3 text-center">3,687</td>
                  <td className="px-4 py-3 text-center">3,738</td>
                  <td className="px-4 py-3 text-center">1,992</td>
                  <td className="px-4 py-3 text-center">65%</td>
                  <td className="px-4 py-3 text-center">35%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2023/24</td>
                  <td className="px-4 py-3 text-center">4,269</td>
                  <td className="px-4 py-3 text-center">4,463</td>
                  <td className="px-4 py-3 text-center">1,900</td>
                  <td className="px-4 py-3 text-center">70%</td>
                  <td className="px-4 py-3 text-center">30%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2022/23</td>
                  <td className="px-4 py-3 text-center">3,274</td>
                  <td className="px-4 py-3 text-center">3,936</td>
                  <td className="px-4 py-3 text-center">1,647</td>
                  <td className="px-4 py-3 text-center">70%</td>
                  <td className="px-4 py-3 text-center">30%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2021/22</td>
                  <td className="px-4 py-3 text-center">3,274</td>
                  <td className="px-4 py-3 text-center">4,615</td>
                  <td className="px-4 py-3 text-center">1,398</td>
                  <td className="px-4 py-3 text-center">77%</td>
                  <td className="px-4 py-3 text-center">23%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2020/21</td>
                  <td className="px-4 py-3 text-center">2,699</td>
                  <td className="px-4 py-3 text-center">4,425</td>
                  <td className="px-4 py-3 text-center">1,612</td>
                  <td className="px-4 py-3 text-center">73%</td>
                  <td className="px-4 py-3 text-center">27%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-amber-500/20 rounded-xl p-6 border border-amber-500/30">
              <h3 className="font-semibold text-white mb-3">The £96m backlog</h3>
              <p className="text-brand-200 text-sm mb-3">
                The council states a £57m carriageway backlog and £39m footway backlog. At 2024/25 total maintenance
                spend of £5.730m, clearing the combined backlog would take roughly 17 years of entire budget — with no
                allowance for new failures.
              </p>
              <p className="text-brand-200 text-sm">
                Since 2024/25, 53% of capital has gone to preventative work and 47% to structural maintenance — yet
                the council admits it cannot fund resurfacing for all structurally failed roads.
              </p>
            </div>
            <div className="bg-rose-500/20 rounded-xl p-6 border border-rose-500/30">
              <h3 className="font-semibold text-white mb-3">Why spend is still AMBER</h3>
              <p className="text-brand-200 text-sm">
                Projected 2025/26 capital spend matches the DfT allocation at £5.283m — but historical spend patterns,
                the admitted backlog, and reactive workload (6,027 pothole repairs in 2024/25) keep the DfT spend
                scorecard at AMBER alongside condition and best practice. Aggregate spend does not prove your specific
                defect was inspected and repaired within reasonable intervals.
              </p>
            </div>
          </div>

          <QuoteCard
            quote="There is currently a £57m maintenance backlog on carriageways and £39m on footways."
            source="Brighton & Hove City Council Local Highways Maintenance Transparency Report (June 2025)"
          />
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Rising Pothole Patch Counts
          </h2>
          <p className="text-surface-600 mb-8">
            Estimated carriageway pothole repairs — the council records repairs in m² and assumes 1m² equals one pothole
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
            <p className="text-amber-800 text-sm">
              <strong>Data caveat:</strong> The 2020/21 figure of 8,353 is an outlier compared with subsequent years
              (3,577 in 2021/22). The council notes repairs are recorded in square metres, converted to pothole equivalents.
              Year-on-year comparisons should treat 2020/21 with caution.
            </p>
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <caption className="sr-only">Brighton & Hove estimated pothole repairs 2020–2025</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Change vs 2022/23</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">8,353</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">Outlier — see caveat</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">3,577</td>
                  <td className="px-6 py-4 text-center text-surface-500">Baseline</td>
                </tr>
                <tr className="border-b border-surface-100 bg-emerald-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">3,163</td>
                  <td className="px-6 py-4 text-center text-emerald-600">−11.6%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">4,805</td>
                  <td className="px-6 py-4 text-center text-amber-600">+51.9%</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">6,027</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">+90.5%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <QuoteCard
              quote="Once defects begin to appear regularly on a road, the road has structurally failed and needs to be resurfaced. There is not enough funding to resurface all the roads that have failed and therefore prioritising the repair of investigation-level defects keeps the road safe until a structural resurface can be funded."
              source="Brighton & Hove City Council Local Highways Maintenance Transparency Report (June 2025)"
            />
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Nearly 17 patches a day</h3>
              <p className="text-surface-600 text-sm">
                6,027 estimated pothole repairs in 2024/25 works out to roughly 16.5 patches per day across 609km.
                That volume of reactive safety work sits alongside 65% preventative spend in the same year — evidence
                of ongoing defect formation, not proof your specific pothole was unavoidable.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Planned work */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Planned Work 2025/26
          </h2>
          <p className="text-surface-600 mb-8">
            Highway maintenance programme agreed at Full Cabinet on 24 April 2025
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">131,327m²</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Preventative surface treatments</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">35,894m²</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Road resurfacing</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">45,104m²</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Large-scale patching</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-amber-600">£5.283m</div>
              <div className="text-sm text-amber-800 font-medium mt-1">DfT grant allocation</div>
              <div className="text-xs text-amber-700 mt-1">71% preventative / 29% reactive split projected</div>
            </div>
          </div>

          <QuoteCard
            quote="To improve the condition of our roads we need long-term investment in our capital-planned maintenance programmes."
            source="Brighton & Hove City Council Local Highways Maintenance Transparency Report (June 2025)"
          />

          <div className="mt-6 bg-white rounded-xl p-6 border border-surface-200">
            <h3 className="font-semibold text-brand-900 mb-3">Coverage maths</h3>
            <p className="text-surface-600 text-sm">
              35,894m² of resurfacing across 609km of carriageway treats a fraction of the network in a single year.
              The council plans up to 35 footways for large-scale patching and admits £96m of backlog. Most of the
              network continues to rely on reactive defect repairs, safety inspections and lifecycle treatments between
              structural resurfacing programmes.
            </p>
          </div>
        </div>
      </section>

      {/* Section 58 and claims */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against an All-Amber City Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Brighton & Hove runs a data-led lifecycle programme — but admits under-recorded deterioration, a £96m backlog and rising patch counts
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council's favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 65–77% preventative spend across recent years — aligned with DfT "prevention is better than cure"
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Full-network AEI from 2025/26 — more frequent condition data than statutory minimums
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Risk-based reactive policy with permanent warm-mix repairs where possible
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Road permit scheme, Section 58 notices on resurfacing, and structured asset management
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a prepared Section 58 defence citing lifecycle planning and inspection policy — not generic neglect.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits SCANNER understated urban deterioration for years before the 2024 AEI reset
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> £96m admitted backlog — structurally failed roads patched until resurfacing is funded
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 6,027 pothole repairs in 2024/25 — up 90.5% from the 2022/23 low of 3,163
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 480km of U-roads (79% of network) — where most claims start
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER on all four DfT scorecards for 2025/26 — regulator shares concerns on maintenance practice
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8 mb-8">
            <h3 className="font-display text-xl font-bold mb-4">Section 41 vs Section 58</h3>
            <p className="text-brand-200 mb-4">
              Under <strong className="text-white">Section 41 of the Highways Act 1980</strong>, Brighton & Hove must
              maintain public highways. To defend a claim under <strong className="text-white">Section 58</strong>, it
              must show a reasonable system for inspecting and repairing the specific defect — not just publish an
              asset-management strategy.
            </p>
            <ul className="text-brand-300 space-y-2 text-sm mb-6">
              <li>• Was your road on the AEI survey cycle — and had condition data been refreshed before your incident?</li>
              <li>• Did the defect meet intervention criteria during routine safety inspections?</li>
              <li>• Were there prior reports (council online form, FixMyStreet) giving actual notice?</li>
              <li>• Does photographic evidence show defect age beyond the council's stated repair interval?</li>
            </ul>
            <p className="text-white font-medium">
              The winning strategy here is specificity: prior-report search, photo assessment, and citations from
              Brighton & Hove's own transparency data — including survey-methodology caveats — where they help your case.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-12 bg-surface-100 border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-3">
            Report a Pothole to Brighton & Hove City Council
          </h2>
          <p className="text-surface-600 mb-6">
            Reporting a defect creates a record the council had notice. Do this before claiming — and tell us
            when you reported it so we can reference it in your pack.
          </p>
          <a
            href="https://www.brighton-hove.gov.uk/travel-and-road-safety/roads-and-highways/roads-pavements-and-potholes-report-damage"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
          >
            Report road or pavement damage — brighton-hove.gov.uk
            <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Brighton & Hove?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            An all-amber scorecard and a survey reset demand a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No AEI methodology context</li>
                <li>• No £96m backlog citation</li>
                <li>• No prior-report search</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ Survey methodology change documented</li>
                <li>✅ £96m backlog and council admissions quoted</li>
                <li>✅ 6,027 pothole repairs in 2024/25 cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Brighton & Hove</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=brighton-and-hove"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Brighton and Hove Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            {faqs.map((faq) => (
              <div key={faq.question} className="bg-white rounded-xl p-6 border border-surface-200">
                <h3 className="font-semibold text-brand-900 mb-2">{faq.question}</h3>
                <p className="text-surface-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.brighton-hove.gov.uk/travel-and-road-safety/roads-and-highways/local-highways-maintenance-transparency-report"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Brighton & Hove City Council Local Highways Maintenance Transparency Report (June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
